import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Clock, Building2 } from "lucide-react";
import CampaignHeader from "@/components/Frontend/components/CampaignHeader";
import CampaignHeadings from "@/components/Frontend/components/CampaignHeadings";
import CampaignBody from "@/components/Frontend/components/CampaignBody";
import { FrontendService } from "@/services/frontendService";
import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import Loader from "@/components/Loader";
import { GoogleFontLoader } from "@/components/GoogleFontLoader";
import { isLightColor, truncateToWords } from "@/utils/helpers";
import CampaignPrePostText from "@/components/Frontend/components/CampaignPrePostText";

function Layout() {
  const { companySlug, campaignSlug } = useParams();
  const [campaign, setCampaign] = useState({});
  const [campaign_headers, setCampignHeaders] = useState({
    name: "",
    logo: "",
  });
  const [campaign_header_sections, setCampignHeaderSections] = useState([{}]);
  const [campaign_body_sections, setCampignBodySections] = useState([{}]);
  const [company, setCompany] = useState({});
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const _campaign = await FrontendService.getCampaignBySlug(
          companySlug,
          campaignSlug,
        );
        if (_campaign) {
          setCampaign(_campaign);
          setCampignHeaders({
            name: _campaign.title,
            logo: _campaign.company?.logo?.url,
          });
          setCampignHeaderSections(
            _campaign?.campaign_fields?.filter(
              (item) => item.field_type == "heading",
            ),
          );
          setCampignBodySections(
            _campaign?.campaign_fields?.filter(
              (item) => item.field_type == "body",
            ),
          );
          setVacancies(_campaign?.vacancies);
          setCompany(_campaign?.company);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching campaign:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [companySlug, campaignSlug]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );

  const checkCampaign = (OnlyCheckCondition = false) => {
    const currentDate = new Date();
    const startDate = new Date(
      campaign?.start_date + " " + campaign?.start_time,
    );
    const endDate = new Date(campaign?.end_date + " " + campaign?.end_time);
    if (currentDate < startDate) {
      if (OnlyCheckCondition) {
        return true;
      }
      return <CampaignPrePostText campaign={campaign} isPreCampaign={true} />;
    }
    if (currentDate > endDate) {
      if (OnlyCheckCondition) {
        return true;
      }
      return <CampaignPrePostText campaign={campaign} isEndCampaign={true} />;
    }
    return null;
  };

  // Add the GoogleFontLoader component
  return (
    <>
      {company?.brand_font && (
        <GoogleFontLoader key={company?.brand_font} font={company.brand_font} />
      )}
      <div
        className="min-h-screen bg-muted p-4 list-style-initial"
        style={{ fontFamily: company?.brand_font || "inherit" }}
      >
        <div className="max-w-7xl mx-auto">
          {/* Logo */}
          <CampaignHeader
            campaign={campaign_headers}
            brand_color={company?.brand_color}
          />
          {checkCampaign()}

          {/* Show vacancies at top if show_top is true */}
          {(() => {
            const showTop = campaign?.show_top;
            const shouldShowTop =
              showTop === true || showTop === 1 || showTop === "1";
            const campaignNotStarted = checkCampaign(true);
            const finalCondition = shouldShowTop && !campaignNotStarted;

            return finalCondition;
          })() && (
            <div
              className={`grid gap-6 mb-8 ${
                (vacancies?.filter(
                  (vacancy) => vacancy.status === "1" || vacancy.status === 1,
                )?.length || 0) === 1
                  ? "grid-cols-1"
                  : "grid-cols-1 md:grid-cols-2"
              }`}
            >              {(() => {
                const filteredVacancies = vacancies?.filter(
                  (vacancy) => vacancy.status === "1" || vacancy.status === 1,
                );
                console.log("Vacancies debug:");
                console.log("All vacancies:", vacancies);
                console.log("Filtered vacancies:", filteredVacancies);
                console.log(
                  "Vacancy statuses:",
                  vacancies?.map((v) => ({
                    id: v.id,
                    status: v.status,
                    type: typeof v.status,
                  })),
                );
                return filteredVacancies;
              })()?.map((vacancy) => (
                <div
                  key={vacancy.id}
                  className="bg-card rounded-xl shadow-lg overflow-hidden"
                >
                  <div
                    style={{
                      background: company?.brand_color || "#000",
                      color: company?.brand_text_color || "#fff",
                    }}
                    className=" p-6"
                  >
                    <h3
                      className={`text-xl font-semibold ${
                        isLightColor(company?.brand_color)
                          ? "text-foreground"
                          : "text-white"
                      } mb-2`}
                    >
                      {vacancy.title}
                    </h3>
                    <div className="flex flex-wrap gap-4">
                      {vacancy?.vacancy_tag?.map((tag, index) => (
                        <div
                          key={index}
                          className="bg-card/10 px-3 py-1 rounded-lg border border-white/20 flex items-center gap-2"
                        >
                          <span
                            className={`text-sm ${
                              isLightColor(company?.brand_color)
                                ? "text-foreground"
                                : "text-white"
                            }`}
                          >
                            {tag.title}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="relative">
                      <div
                        className="microsite-content "
                        dangerouslySetInnerHTML={{
                          __html: expandedDescriptions[vacancy.id]
                            ? vacancy?.description
                            : vacancy?.description?.length > 500 &&
                                truncateToWords(vacancy?.description).length <
                                  vacancy?.description?.length
                              ? truncateToWords(vacancy?.description) + "... "
                              : vacancy?.description,
                        }}
                      />
                      {vacancy?.description?.length > 500 &&
                        truncateToWords(vacancy?.description).length <
                          vacancy?.description?.length &&
                        !expandedDescriptions[vacancy.id] && (
                          <button
                            onClick={() =>
                              setExpandedDescriptions((prev) => ({
                                ...prev,
                                [vacancy.id]: true,
                              }))
                            }
                            className="inline underline font-medium hover:opacity-80"
                          >
                            Read More
                          </button>
                        )}
                      {expandedDescriptions[vacancy.id] &&
                        vacancy?.description?.length > 500 &&
                        truncateToWords(vacancy?.description).length <
                          vacancy?.description?.length && (
                          <div className="mt-2">
                            <button
                              onClick={() =>
                                setExpandedDescriptions((prev) => ({
                                  ...prev,
                                  [vacancy.id]: false,
                                }))
                              }
                              className="bg-card/10 rounded-lg font-medium underline px-3 py-1"
                            >
                              Show Less
                            </button>
                          </div>
                        )}
                    </div>
                    <div className="mt-4">
                      <button
                        onClick={() => {
                          navigate(
                            `/${companySlug}/${campaignSlug}/${vacancy.slug}`,
                            { state: { campaign } },
                          );
                        }}
                        style={{
                          background: company?.brand_color || "#000",
                        }}
                        className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-colors duration-200 ${
                          isLightColor(company?.brand_color)
                            ? "text-foreground"
                            : "text-white"
                        }`}
                      >
                        <span
                          className={`${
                            isLightColor(company?.brand_color)
                              ? "text-foreground"
                              : "text-white"
                          }`}
                        >
                          Apply Now
                        </span>
                        <ArrowRight
                          className={`w-5 h-5 ${
                            isLightColor(company?.brand_color)
                              ? "text-foreground"
                              : "text-white"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <CampaignHeadings
            campaignheadings={campaign_header_sections}
            company={company}
            text_class={
              isLightColor(company?.brand_color) ? "text-foreground" : "text-white"
            }
          />

          <CampaignBody campaignbody={campaign_body_sections} />
          {/** campaign has pre_campaign_text, end_campaign_text, start_date and start_time and end_date and end_time and also time_zone as "UTC -09:00"
           * check if campaign has not started yet then show pre_campaign_text
           * check if campaign has ended then show end_campaign_text
           * check if campaign is running then show vacancies
           * if pre or end text are empty or null then show generic message
           */}
          {campaign?.show_top !== true &&
            campaign?.show_top !== 1 &&
            campaign?.show_top !== "1" &&
            (checkCampaign(true) ?? (
              <div
                className={`grid gap-6 mb-8 ${
                  (vacancies?.filter((vacancy) => vacancy.status == "1")
                    ?.length || 0) === 1
                    ? "grid-cols-1"
                    : "grid-cols-1 md:grid-cols-2"
                }`}
              >                {vacancies
                  ?.filter((vacancy) => vacancy.status == "1")
                  ?.map((vacancy) => (
                    <div
                      key={vacancy.id}
                      className="bg-card rounded-xl shadow-lg overflow-hidden"
                    >
                      <div
                        style={{
                          background: company?.brand_color || "#000",
                          color: company?.brand_text_color || "#fff",
                        }}
                        className=" p-6"
                      >
                        <h3
                          className={`text-xl font-semibold ${
                            isLightColor(company?.brand_color)
                              ? "text-foreground"
                              : "text-white"
                          } mb-2`}
                        >
                          {vacancy.title}
                        </h3>
                        <div className="flex flex-wrap gap-4">
                          {vacancy?.vacancy_tag?.map((tag, index) => (
                            <div
                              key={index}
                              className="bg-card/10 px-3 py-1 rounded-lg border border-white/20 flex items-center gap-2"
                            >
                              <span
                                className={`text-sm ${
                                  isLightColor(company?.brand_color)
                                    ? "text-foreground"
                                    : "text-white"
                                }`}
                              >
                                {tag.title}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="relative">
                          <div
                            className="microsite-content "
                            dangerouslySetInnerHTML={{
                              __html: expandedDescriptions[vacancy.id]
                                ? vacancy?.description
                                : vacancy?.description?.length > 500 &&
                                    truncateToWords(vacancy?.description)
                                      .length < vacancy?.description?.length
                                  ? truncateToWords(vacancy?.description) +
                                    ".... "
                                  : vacancy?.description,
                            }}
                          />
                          {vacancy?.description?.length > 500 &&
                            truncateToWords(vacancy?.description).length <
                              vacancy?.description?.length &&
                            !expandedDescriptions[vacancy.id] && (
                              <button
                                onClick={() =>
                                  setExpandedDescriptions((prev) => ({
                                    ...prev,
                                    [vacancy.id]: true,
                                  }))
                                }
                                className="inline underline font-medium hover:opacity-80"
                              >
                                Read More
                              </button>
                            )}
                          {expandedDescriptions[vacancy.id] &&
                            vacancy?.description?.length > 500 &&
                            truncateToWords(vacancy?.description).length <
                              vacancy?.description?.length && (
                              <div className="mt-2">
                                <button
                                  onClick={() =>
                                    setExpandedDescriptions((prev) => ({
                                      ...prev,
                                      [vacancy.id]: false,
                                    }))
                                  }
                                  className="bg-card/10 rounded-lg font-medium underline  py-1"
                                >
                                  Show Less
                                </button>
                              </div>
                            )}
                        </div>
                        {/* Apply Now button on micrsite */}
                        <div className="mt-4">
                          <button
                            onClick={() => {
                              navigate(
                                `/${companySlug}/${campaignSlug}/${vacancy.slug}`,
                                { state: { campaign } },
                              );
                            }}
                            style={{
                              background: company?.brand_color || "#000",
                            }}
                            className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-colors duration-200 ${
                              isLightColor(company?.brand_color)
                                ? "text-foreground"
                                : "text-white"
                            }`}
                          >
                            <span
                              className={`${
                                isLightColor(company?.brand_color)
                                  ? "text-foreground"
                                  : "text-white"
                              }`}
                            >
                              Apply Now
                            </span>
                            <ArrowRight
                              className={`w-5 h-5 ${
                                isLightColor(company?.brand_color)
                                  ? "text-foreground"
                                  : "text-white"
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ))}
        </div>
      </div>
    </>
  );
}

export default Layout;
